import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  AlertTriangle,
  Activity,
  UtensilsCrossed,
  Wind,
  Thermometer,
  Droplets,
  Shield,
  Users,
  Stethoscope,
  UserCircle,
  Building,
  Mail,
  Phone,
  Bell,
  Send,
  Plus,
  Trash2,
  User
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { getResident, getResidentSettings, updateResidentSettings } from '../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SettingGroup({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
      <h3 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-blue-500" />
        {title}
      </h3>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function SettingField({ label, description, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
      <div className="flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        {description && (
          <p className="text-xs text-[#52525b] mt-0.5">{description}</p>
        )}
      </div>
      <div className="w-full md:w-auto">
        {children}
      </div>
    </div>
  );
}

function ContactCard({ contact, onToggle, onRemove, isDefault }) {
  return (
    <div className={cn(
      "flex items-center justify-between p-4 rounded-lg border transition-colors",
      contact.enabled 
        ? "bg-blue-500/5 border-blue-500/20" 
        : "bg-[#09090b]/50 border-[#27272a]"
    )}>
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          contact.enabled ? "bg-blue-500/10" : "bg-[#27272a]"
        )}>
          {contact.type === 'email' ? (
            <Mail className={cn("h-5 w-5", contact.enabled ? "text-blue-500" : "text-[#52525b]")} />
          ) : (
            <Phone className={cn("h-5 w-5", contact.enabled ? "text-blue-500" : "text-[#52525b]")} />
          )}
        </div>
        <div>
          <div className="font-medium text-sm flex items-center gap-2">
            {contact.name}
            {isDefault && (
              <span className="text-[10px] bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded-full">DEFAULT</span>
            )}
          </div>
          <div className="text-xs text-[#a1a1aa] font-mono">{contact.value}</div>
          {contact.role && (
            <div className="text-[10px] text-[#52525b] mt-0.5">{contact.role}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={contact.enabled}
          onCheckedChange={() => onToggle(contact.id)}
          data-testid={`contact-toggle-${contact.id}`}
        />
        {!isDefault && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(contact.id)}
            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
            data-testid={`contact-remove-${contact.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function RoleToggle({ role, description, enabled }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-[#09090b]/50">
      <div className="flex items-center gap-3">
        {role === 'family' && <Users className="h-5 w-5 text-purple-500" />}
        {role === 'doctor' && <Stethoscope className="h-5 w-5 text-green-500" />}
        {role === 'medical' && <Shield className="h-5 w-5 text-blue-500" />}
        {role === 'concierge' && <Building className="h-5 w-5 text-orange-500" />}
        <div>
          <div className="font-medium text-sm capitalize">{role} View</div>
          <div className="text-xs text-[#52525b]">{description}</div>
        </div>
      </div>
      <Switch checked={enabled} disabled className="opacity-50" />
    </div>
  );
}

export default function Settings() {
  const { id } = useParams();
  const [resident, setResident] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modified, setModified] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingTestSMS, setSendingTestSMS] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  
  // Notification contacts
  const [contacts, setContacts] = useState([
    { id: 'default', name: 'Eldera Support', value: 'info@0gbaltics.com', type: 'email', role: 'Platform Support', enabled: true },
  ]);
  const [newContactName, setNewContactName] = useState('');
  const [newContactValue, setNewContactValue] = useState('');
  const [newContactType, setNewContactType] = useState('email');

  // Default settings if none exist
  const defaultSettings = {
    fall_sensitivity: 'medium',
    fall_immobility_seconds: 45,
    fall_escalate_after_minutes: 3,
    breakfast_enabled: true,
    breakfast_deadline_hour: 10,
    breakfast_deadline_min: 0,
    co2_threshold_ppm: 1200,
    temp_min_c: 18,
    temp_max_c: 26,
    humidity_max_pct: 70,
    activity_low_threshold: 30,
    email_notifications: true,
    sms_notifications: false
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [residentData, settingsData] = await Promise.all([
          getResident(id),
          getResidentSettings(id)
        ]);
        
        setResident(residentData);
        setSettings({ ...defaultSettings, ...settingsData });
        
        // Load saved contacts if any
        if (settingsData?.notification_contacts) {
          setContacts(settingsData.notification_contacts);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        toast.error('Failed to load settings');
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [id]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setModified(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateResidentSettings(id, { 
        ...settings, 
        notification_contacts: contacts 
      });
      toast.success('Settings saved successfully');
      setModified(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(defaultSettings);
    setContacts([
      { id: 'default', name: 'Eldera Support', value: 'info@0gbaltics.com', type: 'email', role: 'Platform Support', enabled: true },
    ]);
    setModified(true);
    toast.info('Settings reset to defaults');
  };

  const handleToggleContact = (contactId) => {
    setContacts(prev => prev.map(c => 
      c.id === contactId ? { ...c, enabled: !c.enabled } : c
    ));
    setModified(true);
  };

  const handleRemoveContact = (contactId) => {
    setContacts(prev => prev.filter(c => c.id !== contactId));
    setModified(true);
    toast.info('Contact removed');
  };

  const handleAddContact = () => {
    if (!newContactName || !newContactValue) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const newContact = {
      id: `contact-${Date.now()}`,
      name: newContactName,
      value: newContactValue,
      type: newContactType,
      role: '',
      enabled: true
    };
    
    setContacts(prev => [...prev, newContact]);
    setNewContactName('');
    setNewContactValue('');
    setModified(true);
    toast.success('Contact added');
  };

  const handleSendTestSMS = async () => {
    // Find first enabled phone contact or use test number
    const enabledPhones = contacts.filter(c => c.enabled && c.type === 'phone');
    const phoneToUse = enabledPhones[0]?.value || testPhoneNumber;
    
    if (!phoneToUse) {
      toast.error('Please add a phone contact or enter a test number');
      return;
    }
    
    try {
      setSendingTestSMS(true);
      const response = await fetch(`${BACKEND_URL}/api/test-sms?to_phone=${encodeURIComponent(phoneToUse)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Test SMS sent to ${phoneToUse}!`);
      } else {
        const error = await response.json();
        toast.error(`Failed: ${error.detail}`);
      }
    } catch (err) {
      console.error('Failed to send test SMS:', err);
      toast.error('Failed to send test SMS');
    } finally {
      setSendingTestSMS(false);
    }
  };

  const handleSendTestEmail = async () => {
    const enabledEmails = contacts.filter(c => c.enabled && c.type === 'email');
    
    if (enabledEmails.length === 0) {
      toast.error('No email contacts enabled');
      return;
    }
    
    try {
      setSendingTestEmail(true);
      
      // Send test email to all enabled contacts
      for (const contact of enabledEmails) {
        const response = await fetch(`${BACKEND_URL}/api/send-alert-notification?alert_type=test&resident_name=${encodeURIComponent(resident?.first_name + ' ' + resident?.last_name || 'Test Resident')}&severity=low&created_at=${new Date().toISOString()}&to_email=${encodeURIComponent(contact.value)}`, {
          method: 'POST'
        });
        
        if (response.ok) {
          toast.success(`Test email sent to ${contact.value}`);
        } else {
          toast.error(`Failed to send to ${contact.value}`);
        }
      }
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error('Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-[#27272a] rounded w-1/3 mb-4" />
          <div className="h-4 bg-[#27272a] rounded w-1/4 mb-8" />
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-40 bg-[#27272a] rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="settings-page">
      {/* Header */}
      <Link to={`/resident/${id}`} className="flex items-center gap-2 text-[#a1a1aa] hover:text-[#fafafa] mb-6">
        <ArrowLeft className="h-4 w-4" />
        Back to Resident
      </Link>
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-barlow font-bold text-2xl md:text-3xl tracking-tight">
            Settings
          </h1>
          {resident && (
            <p className="text-[#a1a1aa] text-sm mt-1">
              {resident.first_name} {resident.last_name}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleReset}
            data-testid="reset-settings"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!modified || saving}
            data-testid="save-settings"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
      
      {modified && (
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-6 flex items-center gap-2 text-orange-500 text-sm">
          <AlertTriangle className="h-4 w-4" />
          You have unsaved changes
        </div>
      )}
      
      <div className="space-y-6">
        {/* Notification Contacts - NEW */}
        <SettingGroup title="Notification Contacts" icon={Users}>
          <p className="text-sm text-[#a1a1aa] mb-4">
            Configure who receives alert escalation notifications. Toggle contacts on/off.
          </p>
          
          {/* Existing Contacts */}
          <div className="space-y-3 mb-6">
            {contacts.map((contact, index) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onToggle={handleToggleContact}
                onRemove={handleRemoveContact}
                isDefault={index === 0}
              />
            ))}
          </div>
          
          {/* Add New Contact */}
          <div className="border border-dashed border-[#27272a] rounded-lg p-4">
            <div className="text-sm font-medium mb-3 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Contact
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Input
                placeholder="Name"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
                className="bg-[#09090b] border-[#27272a]"
                data-testid="new-contact-name"
              />
              <Input
                placeholder={newContactType === 'email' ? 'email@example.com' : '+371 2000 0000'}
                value={newContactValue}
                onChange={(e) => setNewContactValue(e.target.value)}
                className="bg-[#09090b] border-[#27272a]"
                data-testid="new-contact-value"
              />
              <select
                value={newContactType}
                onChange={(e) => setNewContactType(e.target.value)}
                className="bg-[#09090b] border border-[#27272a] rounded-md px-3 py-2 text-sm"
                data-testid="new-contact-type"
              >
                <option value="email">Email</option>
                <option value="phone">Phone (SMS)</option>
              </select>
              <Button onClick={handleAddContact} data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
          
          <Separator className="my-4 bg-[#27272a]" />
          
          {/* Test Notifications */}
          <div className="space-y-4 pt-4 border-t border-[#27272a]">
            <div className="text-sm font-medium">Test Notifications</div>
            
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm">Test Email</div>
                <div className="text-xs text-[#52525b]">Send to all enabled email contacts</div>
              </div>
              <Button
                variant="outline"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                data-testid="test-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
              </Button>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm">Test SMS</div>
                <div className="text-xs text-[#52525b]">Send to phone contacts or test number</div>
              </div>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="+371..."
                  value={testPhoneNumber}
                  onChange={(e) => setTestPhoneNumber(e.target.value)}
                  className="bg-[#09090b] border-[#27272a] w-36 text-sm"
                  data-testid="test-phone-input"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTestSMS}
                  disabled={sendingTestSMS}
                  data-testid="test-sms-btn"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {sendingTestSMS ? 'Sending...' : 'Send Test SMS'}
                </Button>
              </div>
            </div>
          </div>
        </SettingGroup>

        {/* Notification Settings */}
        <SettingGroup title="Notification Preferences" icon={Bell}>
          <SettingField
            label="Email Notifications"
            description="Receive alert escalation emails when alerts are not acknowledged"
          >
            <Switch
              checked={settings?.email_notifications ?? true}
              onCheckedChange={(checked) => handleChange('email_notifications', checked)}
              data-testid="email-notifications-toggle"
            />
          </SettingField>
          
          <SettingField
            label="SMS Notifications"
            description="Receive SMS alerts for critical situations (requires phone contacts)"
          >
            <Switch
              checked={settings?.sms_notifications ?? false}
              onCheckedChange={(checked) => handleChange('sms_notifications', checked)}
              data-testid="sms-notifications-toggle"
            />
          </SettingField>
        </SettingGroup>

        {/* Fall Detection Settings */}
        <SettingGroup title="Fall Detection" icon={AlertTriangle}>
          <SettingField
            label="Sensitivity"
            description="How sensitive the fall detection algorithm should be"
          >
            <select
              value={settings?.fall_sensitivity || 'medium'}
              onChange={(e) => handleChange('fall_sensitivity', e.target.value)}
              className="bg-[#09090b] border border-[#27272a] rounded-md px-3 py-2 text-sm w-full md:w-40"
              data-testid="fall-sensitivity"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </SettingField>
          
          <SettingField
            label="Immobility Threshold"
            description="Seconds of immobility after impact to trigger alert"
          >
            <Input
              type="number"
              value={settings?.fall_immobility_seconds || 45}
              onChange={(e) => handleChange('fall_immobility_seconds', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={10}
              max={120}
              data-testid="fall-immobility"
            />
          </SettingField>
          
          <SettingField
            label="Escalate After (minutes)"
            description="Minutes before sending escalation notification if not acknowledged"
          >
            <Input
              type="number"
              value={settings?.fall_escalate_after_minutes || 3}
              onChange={(e) => handleChange('fall_escalate_after_minutes', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={1}
              max={30}
              data-testid="fall-escalate"
            />
          </SettingField>
        </SettingGroup>
        
        {/* Breakfast Monitoring */}
        <SettingGroup title="Breakfast Monitoring" icon={UtensilsCrossed}>
          <SettingField
            label="Enable Monitoring"
            description="Alert if no fridge activity by deadline"
          >
            <Switch
              checked={settings?.breakfast_enabled ?? true}
              onCheckedChange={(checked) => handleChange('breakfast_enabled', checked)}
              data-testid="breakfast-enabled"
            />
          </SettingField>
          
          <SettingField
            label="Deadline Hour"
            description="Hour by which breakfast should be detected (24h format)"
          >
            <Input
              type="number"
              value={settings?.breakfast_deadline_hour || 10}
              onChange={(e) => handleChange('breakfast_deadline_hour', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={5}
              max={14}
              data-testid="breakfast-hour"
            />
          </SettingField>
          
          <SettingField
            label="Deadline Minutes"
            description="Minutes past the hour"
          >
            <Input
              type="number"
              value={settings?.breakfast_deadline_min || 0}
              onChange={(e) => handleChange('breakfast_deadline_min', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={0}
              max={59}
              data-testid="breakfast-minutes"
            />
          </SettingField>
        </SettingGroup>
        
        {/* Air Quality Thresholds */}
        <SettingGroup title="Air Quality Thresholds" icon={Wind}>
          <SettingField
            label="CO2 Alert Threshold"
            description="PPM level that triggers a warning"
          >
            <Input
              type="number"
              value={settings?.co2_threshold_ppm || 1200}
              onChange={(e) => handleChange('co2_threshold_ppm', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-28"
              min={600}
              max={2000}
              data-testid="co2-threshold"
            />
          </SettingField>
          
          <div className="grid grid-cols-2 gap-4">
            <SettingField
              label="Min Temperature (°C)"
              description="Below this triggers alert"
            >
              <Input
                type="number"
                value={settings?.temp_min_c || 18}
                onChange={(e) => handleChange('temp_min_c', parseInt(e.target.value))}
                className="bg-[#09090b] border-[#27272a] w-full"
                min={10}
                max={25}
                data-testid="temp-min"
              />
            </SettingField>
            
            <SettingField
              label="Max Temperature (°C)"
              description="Above this triggers alert"
            >
              <Input
                type="number"
                value={settings?.temp_max_c || 26}
                onChange={(e) => handleChange('temp_max_c', parseInt(e.target.value))}
                className="bg-[#09090b] border-[#27272a] w-full"
                min={20}
                max={35}
                data-testid="temp-max"
              />
            </SettingField>
          </div>
          
          <SettingField
            label="Max Humidity (%)"
            description="Above this triggers alert"
          >
            <Input
              type="number"
              value={settings?.humidity_max_pct || 70}
              onChange={(e) => handleChange('humidity_max_pct', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={40}
              max={90}
              data-testid="humidity-max"
            />
          </SettingField>
        </SettingGroup>
        
        {/* Activity Monitoring */}
        <SettingGroup title="Activity Monitoring" icon={Activity}>
          <SettingField
            label="Low Activity Threshold"
            description="Score below this triggers a check-in"
          >
            <Input
              type="number"
              value={settings?.activity_low_threshold || 30}
              onChange={(e) => handleChange('activity_low_threshold', parseInt(e.target.value))}
              className="bg-[#09090b] border-[#27272a] w-full md:w-24"
              min={10}
              max={50}
              data-testid="activity-threshold"
            />
          </SettingField>
        </SettingGroup>
        
        {/* Platform Capabilities */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
          <h3 className="font-barlow font-bold text-lg mb-2 flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-blue-500" />
            Platform Capabilities by Role
          </h3>
          <p className="text-sm text-[#52525b] mb-4">
            Configure which roles have access to resident data (read-only preview)
          </p>
          
          <div className="space-y-3">
            <RoleToggle
              role="family"
              description="View basic status, alerts, and activity summary"
              enabled={true}
            />
            <RoleToggle
              role="doctor"
              description="Full medical history, detailed sensor data, notes"
              enabled={true}
            />
            <RoleToggle
              role="medical"
              description="Vitals, medication schedule, visit history"
              enabled={true}
            />
            <RoleToggle
              role="concierge"
              description="Dashboard overview, alert management, basic settings"
              enabled={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
